import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  Banknote,
  BellRing,
  Boxes,
  CalendarDays,
  CalendarRange,
  Camera,
  ClipboardCheck,
  CreditCard,
  Gauge,
  HandCoins,
  Image as ImageIcon,
  LayoutDashboard,
  ListOrdered,
  LogOut,
  MapPin,
  Menu,
  MessageCircle,
  Package,
  PackageCheck,
  PieChart,
  PiggyBank,
  Plus,
  Printer,
  QrCode,
  Receipt,
  Rocket,
  ScanLine,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Store,
  Sunset,
  Tag,
  TrendingUp,
  Truck,
  Users as UsersIcon,
  Wallet,
  X,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { canSee } from "@/admin/RoleGuard";
import { NotificationBell } from "@/admin/NotificationBell";

// [path, label, icon] — allowed roles come from ROUTE_ROLES
const items = [
  ["", "Dashboard", LayoutDashboard],
  ["partner", "Partner Hub", Store],
  ["summary", "Ringkasan Harian", Sunset],
  ["weekly-summary", "Ringkasan Mingguan", CalendarRange],
  ["bookings", "Bookings", ListOrdered],
  ["calendar", "Calendar", CalendarDays],
  ["capture", "Driver Capture", Camera],
  ["ai", "Asisten AI", Sparkles],
  ["whatsapp", "WhatsApp Center", MessageCircle],
  ["reminders", "Reminders", BellRing],
  ["customers", "Customers", UsersIcon],
  ["units", "Rental Units", Boxes],
  ["branches", "Hub Management", MapPin],
  ["packages", "Rental Packages", Package],
  ["promotions", "Promotions", Tag],
  ["delivery-areas", "Delivery Areas", Truck],
  ["addons", "Add-ons", Plus],
  ["payments", "Payments", Wallet],
  ["payment-proofs", "Verifikasi Bukti Transfer", ShieldCheck],
  ["returns", "Pengembalian & Deposit", PackageCheck],
  ["scan", "Scan Aset", ScanLine],
  ["labels", "Label & QR", QrCode],
  ["online-payments", "Pembayaran Online", Receipt],
  ["sandbox-pay", "Test Pembayaran", CreditCard],
  ["site-media", "Banner & Foto", ImageIcon],
  ["reports/margin", "Delivery Margin", TrendingUp],
  ["reports/sources", "Sumber Booking", PieChart],
  ["partner-commissions", "Komisi Partner", HandCoins],
  ["payouts", "Pembayaran Komisi", Banknote],
  ["reports/hub-performance", "Kinerja Hub", Gauge],
  ["reports/deposits", "Laporan Deposit", PiggyBank],
  ["receipts", "Cetak Resi", Printer],
  ["inspections", "Return Inspection", ClipboardCheck],
  ["users", "Staff Accounts", ShieldCheck],
  ["audit-logs", "Audit Log", ScrollText],
  ["go-live", "Checklist Go-Live", Rocket],
  ["settings", "Settings", Settings],
];

// LM Admin PWA — navigasi bawah untuk layar HP
const BOTTOM = [
  ["", "Hari Ini", LayoutDashboard],
  ["calendar", "Kalender", CalendarDays],
  ["scan", "Scan", ScanLine],
  ["bookings", "Order", ListOrdered],
  ["units", "Unit", Boxes],
];

const FINANCE_BOTTOM = [
  ["", "Hari Ini", LayoutDashboard],
  ["bookings", "Order", ListOrdered],
  ["payment-proofs", "Verifikasi", ShieldCheck],
  ["returns", "Deposit", PackageCheck],
  ["reports/deposits", "Laporan", PiggyBank],
];

const ADMIN_BOTTOM = [
  ["", "Hari Ini", LayoutDashboard],
  ["calendar", "Kalender", CalendarDays],
  ["scan", "Scan", ScanLine],
  ["bookings", "Order", ListOrdered],
  ["receipts", "Cetak Resi", Printer],
];

const FINANCE_NAV_ORDER = [
  "",
  "bookings",
  "payment-proofs",
  "returns",
  "reports/deposits",
  "payments",
  "online-payments",
  "reports/margin",
  "reports/sources",
  "partner-commissions",
  "payouts",
  "reports/hub-performance",
  "summary",
  "weekly-summary",
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  let bottomItems = BOTTOM;
  if (user?.role === "finance") {
    bottomItems = FINANCE_BOTTOM;
  } else if (user?.role === "admin") {
    bottomItems = ADMIN_BOTTOM;
  }
  const allowed = items.filter(([p]) => canSee(user, p)).sort(([left], [right]) => {
    if (user?.role !== "finance") return 0;
    return FINANCE_NAV_ORDER.indexOf(left) - FINANCE_NAV_ORDER.indexOf(right);
  });

  const Nav = () => (
    <nav className="space-y-0.5">
      {allowed.map(([p, label, Icon]) => (
        <NavLink key={p} to={`/admin/${p}`} end={p === ""} onClick={() => setOpen(false)}
          data-testid={`admin-nav-${p.replace("/", "-") || "dashboard"}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${isActive ? "bg-lm-red text-white" : "text-cream/70 hover:bg-white/5 hover:text-cream"}`}>
          <Icon size={16} /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-cream flex">
      <aside className="hidden lg:flex flex-col w-60 shrink-0 bg-ink p-4 sticky top-0 h-screen">
        <Link to="/admin" className="font-display text-lg text-cream tracking-tight px-2 py-3">Lapak<span className="text-lm-red">Mahjong</span></Link>
        <div className="mt-4 flex-1 overflow-y-auto"><Nav /></div>
        <div className="pt-4 border-t border-white/10 text-cream/60 text-xs">
          <div className="flex items-center justify-between gap-2 text-cream/70">
            <NotificationBell />
            <button
              data-testid="admin-desktop-lang-toggle"
              onClick={() => setLang(lang === "en" ? "id" : "en")}
              className="rounded-full border border-white/20 px-2 py-1 text-[10px] text-cream hover:border-lm-red"
            >
              {lang === "en" ? "EN" : "ID"}
            </button>
          </div>
          <div className="px-2 truncate">{user?.email}</div>
          <div className="px-2 mt-0.5 text-lm-gold uppercase tracking-widest text-[10px]" data-testid="my-role">{user?.role}</div>
          <button data-testid="logout-btn" onClick={logout} className="mt-2 flex items-center gap-2 px-2 py-2 hover:text-lm-red"><LogOut size={14} /> Logout</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 glass px-4 h-14 flex items-center justify-between">
          <span className="font-display">LapakMahjong Admin</span>
          <div className="flex items-center gap-3">
            <div className="text-ink"><NotificationBell variant="mobile" /></div>
            <button
              data-testid="admin-mobile-lang-toggle"
              onClick={() => setLang(lang === "en" ? "id" : "en")}
              className="rounded-full border border-black/15 px-2 py-1 text-[10px] hover:border-lm-red"
            >
              {lang === "en" ? "EN" : "ID"}
            </button>
            <button data-testid="admin-menu-btn" onClick={() => setOpen(!open)}>{open ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
        </header>
        {open && <div className="lg:hidden bg-ink p-4"><Nav />
          <div className="mt-3 text-cream/50 text-xs px-3">{user?.email} · {user?.role}</div>
          <button onClick={logout} className="mt-2 text-cream/60 text-sm flex items-center gap-2 px-3"><LogOut size={14} /> Logout</button></div>}
        <main className="p-5 md:p-8 max-w-7xl pb-24 lg:pb-8"><Outlet /></main>
        <nav data-testid="admin-bottom-nav"
          className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-ink border-t border-white/10 grid grid-cols-5">
          {bottomItems.filter(([p]) => canSee(user, p)).map(([p, label, Icon]) => (
            <NavLink key={p} to={`/admin/${p}`} end={p === ""} data-testid={`bottom-nav-${p || "today"}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] ${isActive ? "text-lm-red" : "text-cream/60"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
